/* ==========================================================================
   LEV REMEMBERS — supplementary motion for Dawn 15.5.0
   --------------------------------------------------------------------------
   Deliberately small. Dawn already ships:
     - scroll reveal  → assets/animations.js, `scroll-trigger animate--slide-in`,
                        opted into by every section via
                        settings.animations_reveal_on_scroll
     - hover lift     → settings.animations_hover_elements
   Both are enabled in config/settings_data.json. Reimplementing them here
   would duplicate work and, worse, would miss the edge cases Dawn handles —
   theme-editor re-render and `scroll-trigger--cancel` on filtered collection
   grids, which a naive observer leaves permanently invisible.

   So this file adds only what Dawn lacks:
     1. Line-by-line headline mask (the signature motion — his prose is
        already written one thought per line, so the headline assembles the
        way the sentence is written)
     2. Hero parallax

   FAIL-SAFE: the CSS that hides headline lines is scoped to
   `html.lev-motion-ready`, added here at boot. If this script is blocked or
   throws, no text is ever hidden.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ----------------------------------------------------------------------
     Headline mask. Splits a .lev-stack heading on <br> and wipes each line
     up from behind its own overflow-hidden wrapper.
     ---------------------------------------------------------------------- */
  function initHeadlineMask(scope) {
    var root = scope || document;
    var heads = root.querySelectorAll('.lev-stack');

    Array.prototype.forEach.call(heads, function (head) {
      if (head.getAttribute('data-lev-split') === 'done') return;

      // Only split plain text + <br>. Richer markup is left alone rather than
      // risking mangled HTML.
      var kids = Array.prototype.slice.call(head.childNodes);
      var simple = kids.every(function (n) {
        return n.nodeType === 3 || (n.nodeType === 1 && n.tagName === 'BR');
      });
      if (!simple) return;

      var lines = [];
      var buf = '';
      kids.forEach(function (n) {
        if (n.nodeType === 1 && n.tagName === 'BR') { lines.push(buf); buf = ''; }
        else buf += n.textContent;
      });
      lines.push(buf);

      lines = lines.filter(function (l) { return l.trim() !== ''; });
      if (!lines.length) return;

      var frag = document.createDocumentFragment();
      lines.forEach(function (line, i) {
        var outer = document.createElement('span');
        outer.className = 'lev-line';
        var inner = document.createElement('span');
        inner.className = 'lev-line__inner';
        inner.style.transitionDelay = (i * 90) + 'ms';
        inner.textContent = line.trim();   // textContent, not innerHTML
        outer.appendChild(inner);
        frag.appendChild(outer);
      });

      head.innerHTML = '';
      head.appendChild(frag);
      head.setAttribute('data-lev-split', 'done');

      revealWhenSeen(head);
    });
  }

  /* Gate the line reveal on the element actually being in view.
     Without this, every .lev-stack on the page animates on load — so the
     newsletter band's headline finishes its reveal long before the visitor
     scrolls to it, and they only ever see the finished state. Mirrors what
     Dawn's own animations.js does for scroll-trigger elements. */
  var lineObserver = null;

  function revealWhenSeen(el) {
    if (!('IntersectionObserver' in window)) { el.classList.add('lev-lines-in'); return; }

    if (!lineObserver) {
      lineObserver = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          // Next frame, so the initial transformed state is painted first.
          requestAnimationFrame(function () {
            entry.target.classList.add('lev-lines-in');
          });
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    }

    lineObserver.observe(el);
  }

  /* ----------------------------------------------------------------------
     Hero parallax. Transform-free (background-position only), rAF-throttled,
     and skipped below 750px where it costs more than it gives.
     ---------------------------------------------------------------------- */
  function initParallax() {
    var hero = document.querySelector('.lev-hero');
    if (!hero || window.innerWidth < 750) return;

    var ticking = false;

    function update() {
      var rect = hero.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        var shift = Math.max(Math.min(rect.top * -0.12, 60), -60);
        hero.style.setProperty('--lev-parallax', shift.toFixed(2) + 'px');
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function showAllHeadlines() {
    document.documentElement.classList.add('lev-motion-off');
    var lines = document.querySelectorAll('.lev-stack');
    Array.prototype.forEach.call(lines, function (h) { h.classList.add('lev-lines-in'); });
  }

  function boot() {
    document.documentElement.classList.add('lev-motion-ready');

    if (REDUCED.matches) {
      initHeadlineMask();     // still split, so line breaks render — just no motion
      showAllHeadlines();
      return;
    }

    initHeadlineMask();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor rebuilds sections on every edit.
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e.target || document;
    initHeadlineMask(scope);
    if (REDUCED.matches) showAllHeadlines();
  });

  if (REDUCED.addEventListener) {
    REDUCED.addEventListener('change', function (e) {
      if (e.matches) showAllHeadlines();
    });
  }
})();
